import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bell, Check, X } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function NotificationsPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchAll = async () => {
      try {
        const { data } = await api.get("/notifications/me");
        if (!alive) return;
        setItems(data.items || []);
        setUnread(data.unread || 0);
      } catch (e) { console.warn("[Notifications] fetch failed:", e?.message || e); }
    };
    fetchAll();
    const id = setInterval(fetchAll, 60000); // poll every 60s
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!user) return null;

  const markOne = async (n) => {
    if (n.read) return;
    setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.warn("[Notifications] mark-read failed:", e?.message || e); }
  };

  const markAll = async () => {
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch (e) { console.warn("[Notifications] mark-all-read failed:", e?.message || e); }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }} data-testid="notifications-wrap">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="md-header-icon-btn"
        aria-label="Notifications"
        data-testid="header-notifications-btn"
      >
        <Bell size={20} weight="duotone" />
        {unread > 0 && <span className="md-header-icon-badge" data-testid="notification-unread-count">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel" data-testid="notifications-panel">
          <div className="notif-panel-head">
            <strong>Notifications</strong>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs underline" style={{ color: "var(--md-primary)" }} data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="notif-close" aria-label="Close"><X size={12} weight="bold" /></button>
            </div>
          </div>
          <div className="notif-panel-body">
            {items.length === 0 ? (
              <div className="notif-empty" data-testid="notifications-empty">
                <Bell size={28} weight="duotone" style={{ color: "var(--md-text-muted)" }} />
                <p className="mt-2 text-sm">You&apos;re all caught up.</p>
              </div>
            ) : (
              items.map((n) => {
                const inner = (
                  <>
                    <div className="notif-row-content">
                      <div className="notif-row-title">{n.title}</div>
                      <div className="notif-row-body">{n.body}</div>
                      <div className="notif-row-time">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="notif-row-dot" aria-label="Unread" />}
                  </>
                );
                return n.link ? (
                  <Link key={n.id} to={n.link} onClick={() => { markOne(n); setOpen(false); }} className={`notif-row ${n.read ? "" : "notif-row-unread"}`} data-testid={`notif-row-${n.id}`}>{inner}</Link>
                ) : (
                  <button key={n.id} onClick={() => markOne(n)} className={`notif-row ${n.read ? "" : "notif-row-unread"} text-left`} data-testid={`notif-row-${n.id}`}>{inner}</button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
